'use client';

import { useEffect } from 'react';

import { usePathname } from 'next/navigation';

import { safeLocalStorage } from '~/lib/safe-storage';

const AGE_VERIFIED_KEY = 'edem-age-verified';

// /admin, /account: behind auth/staff — the visitor already passed the gate.
// /blog: SFW editorial content. Measured blog CTR was 1.24% vs 13.7% on the
// landers, and every competitor ranking beside our posts serves the same
// listicles ungated — the wall was landing on the article body before the
// first paragraph finished. Product surfaces (catalog, chat, generate,
// landers) all stay gated.
// KEEP IN SYNC with the inline gate script in app/layout.tsx.
const EXEMPT_PREFIXES = ['/admin', '/account', '/blog'];

/**
 * Client-side companion to the SERVER-RENDERED age-gate modal (#age-gate in
 * app/layout.tsx). The modal's visibility is driven entirely by the
 * `age-unverified` class on <html>, set pre-paint by the layout's inline
 * script and toggled by the modal's own inline button listeners — all of
 * which works without React.
 *
 * The one thing only React can do is CLIENT-SIDE NAVIGATION: the inline
 * script runs once per hard load, so a visitor who lands on an exempt route
 * (/blog) and then navigates to a gated one (/catalog) would never see the
 * gate without this component re-evaluating the class per route.
 */
export function AgeVerification() {
  const pathname = usePathname();

  useEffect(() => {
    const isExemptRoute = EXEMPT_PREFIXES.some((p) => pathname?.startsWith(p));
    const verified = safeLocalStorage.getItem(AGE_VERIFIED_KEY) === 'true';
    const gated = !isExemptRoute && !verified;

    // Everything inline, nothing via stylesheet selectors: stale cached CSS
    // chunks both dropped new rules (modal pinned visible sitewide) and kept
    // legacy 'age-unverified' rules alive (ghost '18+' overlay). The class
    // name is the NEW 'age-gate-open' so legacy rules can never match.
    document.documentElement.classList.toggle('age-gate-open', gated);
    const gate = document.getElementById('age-gate');
    if (gate) gate.style.display = gated ? 'flex' : 'none';
    document.body.style.overflow = gated ? 'hidden' : '';
  }, [pathname]);

  return null;
}
